/**
 * Shared analytics privacy policy for HeirWise.
 *
 * One sanitization module used by GA4 and PostHog (Meta keeps its own explicit,
 * minimal payloads). HeirWise serves teens and families, so privacy rules take
 * precedence over analytics richness — but the policy is deliberately precise
 * so that high-value, non-PII taxonomy properties (lesson_name, course_name,
 * module_name, …) survive and remain analyzable in PostHog.
 */

/** Explicit personal-name fields. We do NOT blanket-deny `/name/i`. */
const PERSONAL_NAME_KEYS = new Set([
  "first_name",
  "last_name",
  "full_name",
  "display_name",
  "parent_name",
  "child_name",
  "household_name",
  "recipient_name",
  "user_name",
  "legal_name",
]);

/** Known-safe taxonomy keys that must always pass through. */
const SAFE_TAXONOMY_KEYS = new Set([
  "lesson_name",
  "course_name",
  "module_name",
  "feature_name",
  "event_name",
  "simulator_name",
  "achievement_name",
  "plan_name",
  "flag_name",
  "variant_name",
  "badge_name",
  "screen_name",
  "page_name",
]);

/** Free-text fields — never transmitted. */
const FREE_TEXT_KEYS = new Set([
  "answer",
  "answer_text",
  "response_text",
  "raw_response",
  "private_note",
  "parent_note",
  "note",
  "notes",
  "support_message",
  "message",
  "feedback_text",
  "comment",
  "reason_text",
]);

/** Legacy GA4 denylist keys kept for parity. */
const LEGACY_KEYS = new Set(["email", "email_address", "phone"]);

/**
 * Unambiguous secret / direct-PII patterns. Intentionally narrow — no `/name/i`
 * and no `/amount/i`, both of which would delete useful non-PII properties.
 */
const SENSITIVE_PATTERN =
  /(password|passwd|token|secret|api_key|apikey|authorization|bearer|session_id_raw|email|phone|ssn|street|address|account_number|routing_number|card_number|cvv|dob|birth_date)/i;

/** Strings longer than this are treated as free text and dropped. */
const MAX_STRING_LENGTH = 120;

export function isSensitiveKey(key: string): boolean {
  const k = key.toLowerCase();
  if (SAFE_TAXONOMY_KEYS.has(k)) return false;
  if (PERSONAL_NAME_KEYS.has(k)) return true;
  if (FREE_TEXT_KEYS.has(k)) return true;
  if (LEGACY_KEYS.has(k)) return true;
  return SENSITIVE_PATTERN.test(k);
}

/**
 * Sanitizes an arbitrary property bag before it leaves the browser.
 * Keeps IDs, enums, counts and booleans; drops nested objects, nulls,
 * sensitive keys and long free-text strings.
 */
export function sanitizeAnalyticsProps(
  props: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!props) return {};
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(props)) {
    if (v === undefined || v === null) continue;
    if (isSensitiveKey(k)) continue;
    if (typeof v === "object") continue; // no nested payloads
    if (typeof v === "string" && v.length > MAX_STRING_LENGTH) continue;
    out[k] = v;
  }
  return out;
}

/**
 * Buckets a raw financial value into a coarse band so behavior can be analyzed
 * without transmitting a user's actual balance.
 *
 *   toBand(347.62) -> "0-500"
 */
export function toBand(
  value: number | null | undefined,
  edges: number[] = [0, 500, 1000, 5000, 10000, 50000, 100000],
): string {
  if (value == null || !Number.isFinite(value)) return "unknown";
  if (value < edges[0]) return `<${edges[0]}`;
  for (let i = 0; i < edges.length - 1; i++) {
    if (value < edges[i + 1]) return `${edges[i]}-${edges[i + 1]}`;
  }
  return `${edges[edges.length - 1]}+`;
}

/** Query params that must never be transmitted, even inside a URL string. */
const SENSITIVE_QUERY_PARAMS = [
  "token",
  "access_token",
  "refresh_token",
  "code",
  "email",
  "invite",
  "invite_code",
  "reset",
  "reset_token",
  "otp",
  "apikey",
  "api_key",
  "signature",
];

/**
 * Redacts sensitive query parameters from a URL. Unknown params are preserved
 * (useful for utm_* attribution); known-risky ones become `redacted`.
 * Falls back to stripping the entire query string if the URL can't be parsed.
 */
export function redactUrl(rawUrl: string | undefined | null): string | undefined {
  if (!rawUrl) return rawUrl ?? undefined;
  try {
    const url = new URL(rawUrl, typeof window !== "undefined" ? window.location.origin : "http://localhost");
    for (const param of SENSITIVE_QUERY_PARAMS) {
      if (url.searchParams.has(param)) url.searchParams.set(param, "redacted");
    }
    // Hash fragments can carry Supabase auth tokens — always drop.
    url.hash = "";
    return url.toString();
  } catch {
    return rawUrl.split("?")[0].split("#")[0];
  }
}
